import type {
  CanvasDocumentV3 as CanvasDocument,
  CanvasEdge,
  ReferenceMode,
  ReferenceRole,
  SeedanceNodeSettingsV3 as SeedanceNodeSettings
} from "@seedance/contracts";
import type { ReferenceMapItem, ReferenceMention } from "@seedance/workflow";
import { applyCanvasReferenceCommandV3, resolveTargetReferencesV3, promptToPlainText, type PromptContent } from "@seedance/contracts";
import { parseExternalPromptText } from "./prompts/prompt-edit-operations.js";

type SeedanceNode = Extract<CanvasDocument["nodes"][number], { kind: "seedance" }>;
type TextNode = Extract<CanvasDocument["nodes"][number], { kind: "text" }>;
type ScriptNode = Extract<CanvasDocument["nodes"][number], { kind: "script" }>;
type PromptNode = TextNode | ScriptNode;

export interface PromptInsertionAnalysis {
  seedanceNodeId: string;
  currentPrompt: string;
  insertedText: string;
  originalContent: PromptContent;
  proposedPrompt: string;
}

export interface MentionChange {
  edgeId: string;
  from: ReferenceMention;
  to: ReferenceMention;
}

export interface ReferenceReorderAnalysis {
  seedanceNodeId: string;
  orderedEdgeIds: string[];
  currentPrompt: string;
  proposedPrompt: string;
  mentionChanges: MentionChange[];
}

export interface InvalidEditorReference {
  code: "INVALID_REFERENCE_SOURCE";
  edgeId: string;
  sourceNodeId: string;
  message: string;
}

export interface EditorReferenceAnalysis {
  references: ReferenceMapItem[];
  invalidReferences: InvalidEditorReference[];
}

export interface PromptReference {
  edgeId: string;
  sourceNodeId: string;
  label: string;
  text: string;
  order: number;
}

function findSeedanceNode(
  document: CanvasDocument,
  nodeId: string
): SeedanceNode {
  const node = document.nodes.find(({ id }) => id === nodeId);
  if (node?.kind !== "seedance") {
    throw new Error(`Seedance node not found: ${nodeId}`);
  }
  return node;
}

function updateEdge(
  document: CanvasDocument,
  edgeId: string,
  update: (edge: CanvasEdge) => CanvasEdge
): CanvasDocument {
  let found = false;
  const edges = document.edges.map((edge) => {
    if (edge.id !== edgeId) return edge;
    found = true;
    return update(edge);
  });
  if (!found) throw new Error(`Canvas edge not found: ${edgeId}`);
  return { ...document, edges };
}

export function removeCanvasEdge(
  document: CanvasDocument,
  edgeId: string
): CanvasDocument {
  if (!document.edges.some(edge => edge.id === edgeId)) throw new Error("Canvas edge not found");
  return applyCanvasReferenceCommandV3(document, {type: "delete_edges", edgeIds: [edgeId]});
}

function updateSeedanceNode(
  document: CanvasDocument,
  nodeId: string,
  update: (node: SeedanceNode) => SeedanceNode
): CanvasDocument {
  findSeedanceNode(document, nodeId);
  return {
    ...document,
    nodes: document.nodes.map((node) =>
      node.id === nodeId && node.kind === "seedance" ? update(node) : node
    )
  };
}

export function updateReferenceRole(
  document: CanvasDocument,
  edgeId: string,
  role: ReferenceRole
): CanvasDocument {
  return updateEdge(document, edgeId, (edge) => ({ ...edge, role }));
}

export function updateTextNode(
  document: CanvasDocument,
  nodeId: string,
  text: PromptContent
): CanvasDocument {
  return applyCanvasReferenceCommandV3(document, {type:"set_prompt", source:{nodeId,field:"text",inputEdgeId:null},content:text});
}

export function updateReferenceMode(
  document: CanvasDocument,
  edgeId: string,
  mode: ReferenceMode | undefined
): CanvasDocument {
  return updateEdge(document, edgeId, (edge) => {
    if (mode === undefined) {
      const { mode: _mode, ...withoutMode } = edge;
      return withoutMode;
    }
    return { ...edge, mode };
  });
}

export function updateReferenceInstruction(
  document: CanvasDocument,
  edgeId: string,
  instruction: string
): CanvasDocument {
  return updateEdge(document, edgeId, (edge) => ({ ...edge, instruction }));
}

export function updateReferenceOrder(
  document: CanvasDocument,
  edgeId: string,
  order: number
): CanvasDocument {
  if (!Number.isInteger(order) || order < 0) {
    throw new Error("Reference order must be a non-negative integer");
  }
  return updateEdge(document, edgeId, (edge) => ({ ...edge, order }));
}

export function updateSeedanceSettings(
  document: CanvasDocument,
  nodeId: string,
  patch: Partial<SeedanceNodeSettings>
): CanvasDocument {
  const { prompt, ...parameters } = patch;
  const next = prompt === undefined ? document : applyCanvasReferenceCommandV3(document, {type:"set_prompt",source:{nodeId,field:"prompt",inputEdgeId:null},content:prompt});
  return updateSeedanceNode(next, nodeId, node => ({...node,settings:{...node.settings,...parameters}}));
}

function isPromptNode(node: CanvasDocument["nodes"][number] | undefined): node is PromptNode {
  return node?.kind === "text" || node?.kind === "script";
}

function promptNodeText(node: PromptNode): string {
  return node.kind === "text" ? promptToPlainText(node.text).trim() : [promptToPlainText(node.title), promptToPlainText(node.synopsis)].filter(Boolean).join("\n").trim();
}

export function buildEditorPromptReferences(
  document: CanvasDocument,
  seedanceNodeId: string
): PromptReference[] {
  findSeedanceNode(document, seedanceNodeId);
  const nodesById = new Map(document.nodes.map((node) => [node.id, node]));
  const byKind = { text: 0, script: 0 };
  return document.edges
    .filter(({ targetNodeId }) => targetNodeId === seedanceNodeId)
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id))
    .flatMap((edge) => {
      const node = nodesById.get(edge.sourceNodeId);
      if (!isPromptNode(node)) return [];
      const text = promptNodeText(node);
      // This list owns connected editors, so an empty preview must not remove their source context.
      const index = ++byKind[node.kind];
      return [{
        edgeId: edge.id,
        sourceNodeId: edge.sourceNodeId,
        label: `${node.kind === "text" ? "文本提示词" : "脚本提示词"} ${index}`,
        text,
        order: edge.order
      }];
    })
}

export function buildEditorPrompt(document: CanvasDocument, seedanceNodeId: string): string {
  return resolveTargetReferencesV3(document, seedanceNodeId).finalPrompt;
}

export function analyzeEditorReferences(document: CanvasDocument, seedanceNodeId: string): EditorReferenceAnalysis {
  findSeedanceNode(document, seedanceNodeId);
  const resolved = resolveTargetReferencesV3(document, seedanceNodeId);
  return {references: resolved.references, invalidReferences: []};
}

export function buildEditorReferenceMap(
  document: CanvasDocument,
  seedanceNodeId: string
): ReferenceMapItem[] {
  return analyzeEditorReferences(document, seedanceNodeId).references;
}

function reorderedDocument(
  document: CanvasDocument,
  seedanceNodeId: string,
  orderedEdgeIds: readonly string[]
): CanvasDocument {
  return applyCanvasReferenceCommandV3(document, {type:"reorder_references",targetNodeId:seedanceNodeId,orderedEdgeIds});
}

export function analyzeReferenceReorder(
  document: CanvasDocument,
  seedanceNodeId: string,
  orderedEdgeIds: readonly string[]
): ReferenceReorderAnalysis {
  const node = findSeedanceNode(document, seedanceNodeId);
  const before = buildEditorReferenceMap(document, seedanceNodeId);
  const after = buildEditorReferenceMap(
    reorderedDocument(document, seedanceNodeId, orderedEdgeIds),
    seedanceNodeId
  );

  const afterByEdge = new Map(after.map((reference) => [reference.edgeId, reference]));
  return {
    seedanceNodeId,
    orderedEdgeIds: [...orderedEdgeIds],
    currentPrompt: buildEditorPrompt(document, seedanceNodeId),
    proposedPrompt: buildEditorPrompt(reorderedDocument(document, seedanceNodeId, orderedEdgeIds), seedanceNodeId),
    mentionChanges: before.flatMap((reference) => {
      const next = afterByEdge.get(reference.edgeId);
      return next !== undefined && next.mention !== reference.mention
        ? [{ edgeId: reference.edgeId, from: reference.mention, to: next.mention }]
        : [];
    })
  };
}

export function applyReferenceReorder(
  document: CanvasDocument,
  analysis: ReferenceReorderAnalysis
): CanvasDocument {
  return reorderedDocument(document, analysis.seedanceNodeId, analysis.orderedEdgeIds);
}

export function analyzePromptInsertion(
  document: CanvasDocument,
  seedanceNodeId: string,
  insertedText: string
): PromptInsertionAnalysis {
  const content = findSeedanceNode(document, seedanceNodeId).settings.prompt;
  const currentPrompt = promptToPlainText(content);
  return {
    seedanceNodeId,
    currentPrompt,
    insertedText,
    originalContent: structuredClone(content),
    proposedPrompt:
      currentPrompt.length === 0 ? insertedText : `${currentPrompt}\n${insertedText}`
  };
}

export function applyPromptInsertion(
  document: CanvasDocument,
  seedanceNodeId: string,
  analysis: PromptInsertionAnalysis
): CanvasDocument {
  const node = findSeedanceNode(document, seedanceNodeId);
  if (seedanceNodeId !== analysis.seedanceNodeId || JSON.stringify(node.settings.prompt) !== JSON.stringify(analysis.originalContent)) {
    throw new Error("Prompt changed after the insertion was proposed");
  }
  return updateSeedanceSettings(document, seedanceNodeId, {
    prompt: {segments:[...node.settings.prompt.segments, ...(node.settings.prompt.segments.length ? [{type:"text" as const,text:"\n"}] : []), ...parseExternalPromptText(analysis.insertedText, () => crypto.randomUUID()).segments]}
  });
}
